import clsx from "clsx";
import { useId } from "react";
import type { CommentView } from "../../../../shared/contracts.js";
import { formatInstant } from "./PartsCaseInspector.js";

/**
 * A case's comments (spec §13): append-only, oldest first, each with its
 * author and dealership-time timestamp. The panel only shows what the server
 * returned — no edit or delete affordance exists because no such route does.
 *
 * Shared by the Surface detail and available to the Parts inspector; the
 * composer that adds a comment is a separate component so the panel itself
 * stays pure content.
 */

export interface CommentsPanelProps {
  comments: readonly CommentView[];
  /** The comment to highlight — the one the person just added. */
  highlightCommentId?: string | null;
  className?: string;
}

export function CommentsPanel({
  comments,
  highlightCommentId = null,
  className,
}: CommentsPanelProps) {
  const headingId = useId();

  return (
    <section
      className={clsx("comments", className)}
      aria-labelledby={headingId}
      data-count={comments.length}
    >
      <h3 id={headingId} className="comments__title">
        Comments <span className="comments__count">({comments.length})</span>
      </h3>
      {comments.length === 0 ? (
        <p className="comments__empty">
          No comments yet. Anyone on the team can add one.
        </p>
      ) : (
        <ol className="comments__list">
          {comments.map((comment) => (
            <li
              key={comment.id}
              className="comment"
              data-comment-id={comment.id}
              data-highlight={comment.id === highlightCommentId ? "true" : undefined}
            >
              <p className="comment__meta">
                <span className="comment__author">{comment.authorName}</span>
                <time className="comment__time" dateTime={comment.createdAt}>
                  {formatInstant(comment.createdAt)}
                </time>
              </p>
              <p className="comment__body">{comment.body}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
